import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2, KeyRound } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import logoWhite from '@/assets/logow.svg';
import symposiumText from '@/assets/symposiam.svg';
import bgLogin from '@/assets/bglogin.jpg';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('ลิงก์รีเซ็ตรหัสผ่านไม่ถูกต้อง กรุณาขอลิงก์ใหม่อีกครั้ง');
    }
  }, [token]);

  const getPasswordStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 6) return { label: 'อ่อนมาก', color: '#ef4444', width: '20%' };
    if (pw.length < 8) return { label: 'อ่อน', color: '#f97316', width: '40%' };
    if (!/[A-Z]/.test(pw) || !/[0-9]/.test(pw)) return { label: 'ปานกลาง', color: '#eab308', width: '65%' };
    return { label: 'แข็งแกร่ง', color: '#22c55e', width: '100%' };
  };

  const strength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }
    if (password.length < 8) {
      setStatus('error');
      setMessage('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร');
      return;
    }

    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setMessage(data.message || 'รีเซ็ตรหัสผ่านสำเร็จ');
      } else {
        setStatus('error');
        setMessage(data.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
      }
    } catch {
      setStatus('error');
      setMessage('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#8B7DC3] via-[#B3FFD1] to-[#BFB4EE] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#533193] rounded-full opacity-20 -translate-x-48 -translate-y-48" />
      <div className="absolute top-20 right-0 w-64 h-64 bg-[#533193] rounded-full opacity-30 translate-x-32 -translate-y-12" />
      <div className="absolute bottom-0 left-20 w-80 h-80 bg-[#BFB4EE] rounded-full opacity-25 -translate-y-20" />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: 'spring' }}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full relative z-10"
        style={{ height: '600px' }}
      >
        <div className="flex h-full">
          {/* Left panel */}
          <div className="flex-1 relative overflow-hidden hidden md:block">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(83,49,147,0.7) 0%, rgba(191,180,238,0.3) 100%), url(${bgLogin})`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#533193]/50 to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm border border-white/30">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-white text-2xl font-bold mb-2">ตั้งรหัสผ่านใหม่</h2>
              <p className="text-white/80 text-sm leading-relaxed">
                สร้างรหัสผ่านใหม่ที่แข็งแกร่ง<br />
                เพื่อความปลอดภัยของบัญชีคุณ
              </p>
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 flex flex-col bg-white relative overflow-y-auto">
            {/* Logo — fixed at top-right */}
            <div className="flex justify-end px-8 pt-6 pb-2 flex-shrink-0">
              <div className="flex flex-col items-end">
                <img src={logoWhite} alt="SACIT" className="h-6 w-auto mb-1 filter brightness-0" />
                <img src={symposiumText} alt="Symposium" className="h-7 w-auto filter brightness-0" />
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-10 pb-8">
            <div className="max-w-sm w-full">
              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-6"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-9 h-9 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">เสร็จเรียบร้อย!</h2>
                  <p className="text-gray-500 text-sm mb-8 leading-relaxed">{message}</p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-[#533193] to-[#8B7DC3] text-white py-3 rounded-xl font-bold text-base"
                  >
                    เข้าสู่ระบบ
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h1 className="text-3xl font-didot text-[#533193] mb-2 text-center">Reset Password</h1>
                  <p className="text-gray-500 text-sm text-center mb-7">ตั้งรหัสผ่านใหม่สำหรับบัญชีของคุณ</p>

                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 flex items-start gap-2 text-sm text-red-600"
                    >
                      <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{message}</span>
                    </motion.div>
                  )}

                  {!token ? (
                    <div className="text-center mt-4">
                      <Link to="/login" className="text-[#533193] hover:underline text-sm">
                        ← กลับหน้า Login
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* New password */}
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">รหัสผ่านใหม่</label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); if (status === 'error') setStatus('idle'); }}
                            placeholder="อย่างน้อย 8 ตัวอักษร"
                            className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#533193] focus:ring-0"
                            required
                            disabled={status === 'loading'}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {/* Password strength bar */}
                        {password && strength && (
                          <div className="space-y-1 pt-1">
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full rounded-full"
                                style={{ background: strength.color }}
                                initial={{ width: 0 }}
                                animate={{ width: strength.width }}
                                transition={{ duration: 0.35 }}
                              />
                            </div>
                            <p className="text-xs" style={{ color: strength.color }}>ความแข็งแกร่ง: {strength.label}</p>
                          </div>
                        )}
                      </div>

                      {/* Confirm password */}
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">ยืนยันรหัสผ่านใหม่</label>
                        <div className="relative">
                          <Input
                            type={showConfirm ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="กรอกรหัสผ่านอีกครั้ง"
                            className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:ring-0 ${
                              confirmPassword && confirmPassword !== password
                                ? 'border-red-300 focus:border-red-400'
                                : confirmPassword && confirmPassword === password
                                ? 'border-green-300 focus:border-green-400'
                                : 'border-gray-200 focus:border-[#533193]'
                            }`}
                            required
                            disabled={status === 'loading'}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {confirmPassword && confirmPassword !== password && (
                          <p className="text-xs text-red-500">รหัสผ่านไม่ตรงกัน</p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        disabled={status === 'loading' || !password || !confirmPassword}
                        className="w-full bg-gradient-to-r from-[#533193] to-[#8B7DC3] hover:opacity-90 text-white py-3 rounded-xl font-bold text-base shadow-lg disabled:opacity-50 mt-2"
                      >
                        {status === 'loading'
                          ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> กำลังรีเซ็ต...</span>
                          : 'ยืนยันรหัสผ่านใหม่'
                        }
                      </Button>

                      <div className="text-center pt-1">
                        <Link to="/login" className="text-sm text-gray-500 hover:text-[#533193] transition">
                          ← กลับหน้า Login
                        </Link>
                      </div>
                    </form>
                  )}
                </motion.div>
              )}
            </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
